<template>
  <div class="container mt-4 mb-5">
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border"></div>
      <p class="text-muted mt-2">Loading report...</p>
    </div>
    <div v-else>
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <div v-if="report">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">Support Calls by Job</h5>
          <div>
            <button class="btn btn-primary btn-sm" @click="doPrint">Print</button>
          </div>
        </div>

        <div class="card mb-3">
          <div class="card-body">
             <div>
               <img :src="isLightMode ? require('@/assets/logo-nav.png') : require('@/assets/logo-nav-dark.png')" width="180" />
            </div>
            <table class="table table-bordered fSmall report-table">
              <tbody>
                <tr>
                  <th>Job Name</th>
                  <td>{{ report.jobName }}</td>
                </tr>
                <tr>
                  <th>Range Start</th>
                  <td>{{ formatDate(report.rangeStart) }}</td>
                </tr>
                <tr>
                  <th>Range End</th>
                  <td>{{ formatDate(report.rangeEnd) }}</td>
                </tr>
                <tr>
                  <th>Total hours</th>
                  <td>{{ Number(report.totalHours || 0).toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>

            <h6 class="mt-4">Details</h6>
            <div class="table-responsive" v-if="report.remoteHistory && report.remoteHistory.length">
              <table class="table table-sm table-striped">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Total hours</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, idx) in report.remoteHistory" :key="r.id">
                    <td>{{ idx + 1 }}</td>
                    <td>{{ r.startDate }}</td>
                    <td>{{ r.endDate }}</td>
                    <td>{{ Number(r.totalHours || 0).toFixed(2) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="text-muted" v-else>No details in selected range.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Print area: reuse similar layout as SupportCalls.vue report -->
    <div class="container-fluid" id="print-area-report" v-if="report && showPrintArea" >
      <div class="row">
        <div class="col-lg-12">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <div>
              <img :src="isLightMode ? require('@/assets/logo-nav.png') : require('@/assets/logo-nav-dark.png')" width="180" />
            </div>
            <div class="text-center">
              <img :src="qrDataReport" alt="Generated QR" style="height: 150px; border-style: solid; border-width: 0.6rem;" />
              <div class="fw-bold" style="font-size: 1.5rem;">Support Calls by Job</div>
            </div>
          </div>
          <h5>Summary</h5>
          <table class="table table-bordered fSmall">
            <tbody>
              <tr>
                <th>Job Name</th>
                <td>{{ report.jobName }}</td>
              </tr>
              <tr>
                <th>Range Start</th>
                <td>{{ formatDate(report.rangeStart) }}</td>
              </tr>
              <tr>
                <th>Range End</th>
                <td>{{ formatDate(report.rangeEnd) }}</td>
              </tr>
              <tr>
                <th>Total hours</th>
                <td>{{ Number(report.totalHours || 0).toFixed(2) }}</td>
              </tr>
            </tbody>
          </table>
          <h6 class="mt-4" v-if="report.remoteHistory && report.remoteHistory.length">Details</h6>
          <table class="table table-sm table-striped" v-if="report.remoteHistory && report.remoteHistory.length">
            <thead>
              <tr>
                <th>#</th>
                <th>From</th>
                <th>To</th>
                <th>Total hours</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, idx) in report.remoteHistory" :key="r.id">
                <td>{{ idx + 1 }}</td>
                <td>{{ r.startDate }}</td>
                <td>{{ r.endDate }}</td>
                <td>{{ Number(r.totalHours || 0).toFixed(2) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import printJS from 'print-js'
import QRCode from 'qrcode'
var config = require('@/config/api.json')
const API = config.api
const API2 = config.apiManager; 

export default {
  name: 'PrintRemoteCalls',
  props: ['jobName', 'startDate', 'endDate'],
  data() {
    return {
      loading: true,
      error: '',
      report: null,
      qrDataReport: null,
      isLightMode: false,
      showPrintArea: false
    }
  },
  created() {
    const light = localStorage.getItem('light')
    document.body.classList.toggle('light-mode', false)
    document.body.classList.toggle('light-mode', true)
    if (light !== null) {
      document.body.classList.toggle('light-mode', light)
      this.isLightMode = light
    } else {
      document.body.classList.toggle('light-mode', false)
    }
  },
  async mounted() {
    // Init light mode like Navbar.vue
    const light = localStorage.getItem('light')
    if (light !== null) {
      this.isLightMode = (light === 'true' || light === true)
    } else {
      this.isLightMode = false
    }

    await this.loadReport()

  },
  methods: {
    async buildReportQr(jobName, startDate, endDate) {
      try {
        const url = `${API2}/printRemoteCalls/${encodeURIComponent(jobName)}/${encodeURIComponent(startDate)}/${encodeURIComponent(endDate)}`
        this.qrDataReport = await QRCode.toDataURL(url)
      } catch (e) {
        this.qrDataReport = null
      }
    },
    async loadReport() {
      // Validate route params
      if (!this.jobName || !this.startDate || !this.endDate) {
        this.error = 'Corrupt data: missing parameters.'
        this.loading = false
        return
      }
      try {
        const params = { jobName: this.jobName, startDate: this.startDate, endDate: this.endDate }
        const res = await axios.get(`${API}/get-by-jobaname-dates`, { params })
        if (!res.data || typeof res.data !== 'object') {
          this.error = 'Corrupt data received.'
          this.loading = false
          return
        }
        this.report = res.data
        await this.buildReportQr(this.jobName, this.startDate, this.endDate)
      } catch (e) {
        this.error = 'Error loading report.'
      } finally {
        this.loading = false
      }
    },
    doPrint() {
      if (!this.report) return
      // Mostrar el área de impresión, esperar render y luego imprimir
      this.showPrintArea = true
      this.$nextTick(() => {
        setTimeout(() => {
          printJS({ printable: 'print-area-report', type: 'html', targetStyles: ['*'], scanStyles: true })
          // Ocultar nuevamente el área de impresión después de abrir el diálogo
          setTimeout(() => { this.showPrintArea = false }, 50)
        }, 50)
      })
    },
    formatDate(d) {
      try {
        const dt = new Date(d)
        if (Number.isNaN(dt.getTime())) return d
        const yyyy = dt.getFullYear()
        const mm = String(dt.getMonth() + 1).padStart(2, '0')
        const dd = String(dt.getDate()).padStart(2, '0')
        const hh = String(dt.getHours()).padStart(2, '0')
        const mi = String(dt.getMinutes()).padStart(2, '0')
        return `${yyyy}-${mm}-${dd} ${hh}:${mi}`
      } catch (e) {
        return d
      }
    }
  }
}
</script>

<style>
.report-table {
  table-layout: fixed;
  width: 100%;
}
.report-table th { width: 240px; }
#print-area-report table th { width: 240px; vertical-align: top; }
#print-area-report table td { padding-left: 16px; white-space: nowrap; }

/* Ocultar en pantalla, mostrar solo al imprimir */
@media screen {
  #print-area-report { display: block !important; }
}
@media print {
  #print-area-report { display: block !important; }
}
</style>

